import Nav from "@/components/Nav";
import PageHero from "@/components/PageHero";
import MarketingFooter from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { Feather, ArrowRight } from "lucide-react";
import { usePageMeta } from "@/lib/pageMeta";
import { motion } from "framer-motion";

const FOUNDER_PORTRAIT = "https://images.unsplash.com/photo-1656221010175-bcfeadcb6017?fm=jpg&w=400&auto=format&fit=crop&q=85";

const BELIEFS = [
  {
    title: "Marriage is a decision, not a discovery.",
    body: "Endless swiping treats marriage like shopping. Serial dating treats it like sampling. We treat it like the decision it is — one of the two or three most important choices a human being ever makes.",
  },
  {
    title: "Tradition and science are not enemies.",
    body: "The Kundali is 5,000 years of wisdom. Modern psychology is 60 years of evidence. Both are trying to answer the same question — will this bond last, deepen, and hold? Anubandh.ai holds both hands.",
  },
  {
    title: "Family is not a barrier. It's the blessing.",
    body: "In India, a good marriage isn't two people saying yes. It's two families weaving a shared future. Our product treats family as a first-class user — not an obstacle to route around.",
  },
  {
    title: "Clarity, not choice, is the luxury.",
    body: "The old matrimony portals sold access — thousands of profiles you'd never seriously consider. Dating apps sell dopamine. We sell clarity: a small, verified, thoughtful set of matches, each with a reason.",
  },
  {
    title: "Explainability is not optional.",
    body: "If our AI can't explain why it thinks you and someone are compatible, we haven't built AI. We've built a slot machine. Every score at Anubandh.ai comes with a paragraph — because you deserve to see the reasoning.",
  },
  {
    title: "This is a life. Not a subscription.",
    body: "We would rather have one thoughtful user who found their bond in nine days than a thousand who stayed for the recurring bill. Our product is designed to be uninstalled — the moment you've read your bond correctly.",
  },
];

export default function Philosophy() {
  usePageMeta(
    "Philosophy & Founder Note",
    "A note from the founder K. Singh on why Anubandh.ai exists — and why marriage decisions deserve intelligence, not endless scrolling."
  );
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav variant="marketing" />
      <PageHero
        overline="The Anubandh Philosophy"
        sanskrit="अनुबंध · दर्शन"
        title={<>Six beliefs that shaped<br /><span className="italic text-amber-300">everything we built.</span></>}
        subtitle="Anubandh means &ldquo;the bond that is written&rdquo; — the thread connecting two souls across time and lifetimes. This is why we built the platform, and what we believe about the decision itself."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/how-it-works" className="btn-pill bg-amber-500 text-[#12103A] text-sm px-6 py-3 font-medium inline-flex items-center gap-2">
            See how it works <ArrowRight size={16} />
          </Link>
          <Link to="/signup" data-testid="philosophy-signup" className="btn-pill border border-white/25 text-white text-sm px-6 py-3 hover:bg-white/10">Begin free</Link>
        </div>
      </PageHero>

      {/* Beliefs */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="space-y-14">
          {BELIEFS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.05 }}
              className="grid grid-cols-1 sm:grid-cols-12 gap-6 items-start"
            >
              <div className="sm:col-span-2 font-serif-display text-4xl text-amber-600/70 leading-none">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="sm:col-span-10">
                <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1E1B4B] leading-tight">{b.title}</h2>
                <p className="mt-3 text-stone-700 leading-relaxed text-[15px]">{b.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Founder note */}
      <section className="py-20 bg-white border-t border-stone-200">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Feather className="mx-auto text-amber-600" />
          <div className="overline mt-4">A note from the founder</div>
          <p className="mt-8 font-serif-display text-2xl lg:text-3xl text-[#1E1B4B] leading-relaxed italic">
            &ldquo;We built Anubandh.ai because we watched too many good people marry into
            confusion. India deserves a matchmaker that carries the weight of the decision — not
            the burden of a subscription.&rdquo;
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-500 bg-amber-50">
              <img src={FOUNDER_PORTRAIT} alt="K. Singh" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium text-[#1E1B4B]">K. Singh</div>
              <div className="text-xs text-stone-500">Founder, Anubandh.ai · Greater Noida</div>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
